import { useState } from "react";
import type { RoleRecord } from "../../shared/models";
import { apiRequest } from "../lib/api";

interface OnboardCrewMemberPageProps {
  roles: RoleRecord[];
  onSent: () => Promise<void> | void;
}

export function OnboardCrewMemberPage({ roles, onSent }: OnboardCrewMemberPageProps) {
  const [form, setForm] = useState({
    name: "",
    email: "",
    role_ids: [] as number[],
  });
  const [message, setMessage] = useState<string | null>(null);
  const activeRoles = roles.filter((role) => role.active === 1);

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    try {
      const response = await apiRequest<{ throttled: boolean }>("/api/admin/onboard-crew", {
        method: "POST",
        body: JSON.stringify(form),
      });
      await onSent();
      setMessage(response.throttled
        ? "Crew member updated. A recent code is still active, so a new code was not sent yet."
        : "Crew member onboarded and login code sent.");
      setForm({ name: "", email: "", role_ids: [] });
    } catch (caught) {
      setMessage(caught instanceof Error ? caught.message : "Could not send login code");
    }
  }

  return (
    <div className="stack page">
      <div className="page-header">
        <h2>Onboard Crew Member</h2>
        <p>Create or update a crew account, then send a 6-digit login code by email.</p>
      </div>

      <form className="panel form-grid" onSubmit={submit}>
        <label>
          Name
          <input value={form.name} onChange={(event) => setForm((current) => ({ ...current, name: event.target.value }))} />
        </label>
        <label>
          Email
          <input value={form.email} onChange={(event) => setForm((current) => ({ ...current, email: event.target.value }))} />
        </label>
        <div className="full-span">
          <label>Preselected Roles</label>
          <div className="role-checkboxes">
            {activeRoles.map((role) => (
              <label key={role.id} className="checkbox-chip">
                <input
                  type="checkbox"
                  checked={form.role_ids.includes(role.id)}
                  onChange={(event) => setForm((current) => ({
                    ...current,
                    role_ids: event.target.checked
                      ? [...current.role_ids, role.id]
                      : current.role_ids.filter((value) => value !== role.id),
                  }))}
                />
                <span>{role.name}</span>
              </label>
            ))}
          </div>
        </div>
        <button type="submit">Send Login Code</button>
      </form>

      {message ? <p className={message.includes("sent") ? "success-banner" : "error-banner"}>{message}</p> : null}
    </div>
  );
}
